export const MAX_CONVERSATION_PROGRESS_ITEMS = 3;

const cleanText = value => (typeof value === 'string' ? value.trim() : '');

const uniqueBy = (items, keySelector) => {
  const seen = new Set();
  return items.filter(item => {
    const key = keySelector(item).toLocaleLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const flattenProgressItems = value => {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== 'object') return [];

  return Object.entries(value)
    .sort(([left], [right]) => left.localeCompare(right))
    .flatMap(([, items]) => (
      Array.isArray(items) ? items : items && typeof items === 'object' ? [items] : []
    ));
};

export const isLearningActivity = activity => (
  typeof activity === 'string' && /^learning-path-\d+$/.test(activity)
);

// Accent- and punctuation-insensitive comparison, so "l'hôpital" matches the
// vocabulary entry "hopital". Mirrors normalizeMatchText in
// functions/common/activityProgress.js — keep the two in step.
export const normalizeMatchText = value => (typeof value === 'string' ? value : '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLocaleLowerCase()
  .replace(/[^\p{L}\p{N}]+/gu, ' ')
  .trim();

/** True when `term` appears in `value` as a whole word (after normalization). */
export const containsTerm = (value, term) => {
  const normalizedTerm = normalizeMatchText(term);
  return Boolean(normalizedTerm && ` ${normalizeMatchText(value)} `.includes(` ${normalizedTerm} `));
};

const normalizeConversationProgressWithLimit = (value, itemLimit) => {
  if (value === null || value === undefined) return null;
  let parsedValue = value;
  if (typeof value === 'string' && value.trim().startsWith('{')) {
    try {
      parsedValue = JSON.parse(value);
    } catch (error) {
      return null;
    }
  }
  const structured = typeof parsedValue === 'object' && !Array.isArray(parsedValue) ? parsedValue : {};
  const rawProgress = Number(
    structured.progressIndex ?? structured.progress_index ?? structured.percentage ?? parsedValue
  );
  if (!Number.isFinite(rawProgress)) return null;

  const errors = uniqueBy(flattenProgressItems(structured.errors).map(error => ({
    text: cleanText(error?.text || error?.original || error?.originalText),
    correction: cleanText(error?.correction || error?.corrected || error?.correctionText),
    explanation: cleanText(error?.explanation || error?.reason),
  })).filter(error => error.text && error.correction), error => error.text)
    .slice(0, itemLimit);

  const rawConcepts = structured.acquiredConcepts || structured.acquired_concepts || structured.concepts;
  const acquiredConcepts = uniqueBy(flattenProgressItems(rawConcepts).map(concept => ({
    concept: cleanText(concept?.concept || concept?.label || concept?.text),
    evidence: cleanText(concept?.evidence || concept?.evidenceText || concept?.text),
    status: cleanText(concept?.status).toLowerCase() === 'reviewed' ? 'reviewed' : 'learned',
  })).filter(concept => concept.concept), concept => concept.concept)
    .slice(0, itemLimit);

  return {
    progressIndex: Math.max(0, Math.min(100, Math.round(rawProgress))),
    errors,
    acquiredConcepts,
  };
};

export const normalizeConversationProgress = value => (
  normalizeConversationProgressWithLimit(value, MAX_CONVERSATION_PROGRESS_ITEMS)
);

export const getConversationProgressIndex = value => (
  normalizeConversationProgress(value)?.progressIndex ?? null
);

export const mergeConversationProgress = (...values) => {
  const normalized = values
    .flat()
    .map(value => normalizeConversationProgressWithLimit(value, Infinity))
    .filter(Boolean);
  if (normalized.length === 0) return null;

  return {
    progressIndex: Math.max(...normalized.map(progress => progress.progressIndex)),
    errors: uniqueBy(
      normalized.flatMap(progress => progress.errors),
      error => `${error.text}\u0000${error.correction}`
    ),
    acquiredConcepts: uniqueBy(
      normalized.flatMap(progress => progress.acquiredConcepts),
      concept => concept.concept
    ),
  };
};

const findMatches = (text, role, progress) => {
  const candidates = [];
  const addCandidate = (needle, type, tooltip, priority) => {
    if (!needle) return;
    const lowerText = text.toLocaleLowerCase();
    const lowerNeedle = needle.toLocaleLowerCase();
    let fromIndex = 0;
    while (fromIndex < lowerText.length) {
      const start = lowerText.indexOf(lowerNeedle, fromIndex);
      if (start < 0) break;
      candidates.push({ start, end: start + needle.length, type, tooltip, priority });
      fromIndex = start + needle.length;
    }
  };

  progress.errors.forEach(error => {
    if (role === 'user') {
      addCandidate(error.text, 'error', error.explanation || `Try: ${error.correction}`, 4);
    } else if (role === 'assistant') {
      addCandidate(error.correction, 'correction', error.explanation || 'AI correction', 3);
    }
  });

  progress.acquiredConcepts.forEach(concept => {
    addCandidate(
      concept.evidence || concept.concept,
      concept.status === 'reviewed' ? 'reviewed' : 'learned',
      concept.status === 'reviewed' ? `Reviewed: ${concept.concept}` : `First learned: ${concept.concept}`,
      concept.status === 'reviewed' ? 1 : 2
    );
  });

  return candidates
    .sort((a, b) => a.start - b.start || b.priority - a.priority || (b.end - b.start) - (a.end - a.start))
    .filter((candidate, index, all) => !all.slice(0, index).some(kept => (
      candidate.start < kept.end && candidate.end > kept.start
    )));
};

export const segmentConversationText = (text, role, value) => {
  const progress = mergeConversationProgress(value);
  if (!progress || typeof text !== 'string') return [{ text: text || '', type: null }];
  const matches = findMatches(text, role, progress).sort((a, b) => a.start - b.start);
  if (matches.length === 0) return [{ text, type: null }];

  const segments = [];
  let cursor = 0;
  matches.forEach(match => {
    if (match.start > cursor) segments.push({ text: text.slice(cursor, match.start), type: null });
    segments.push({ text: text.slice(match.start, match.end), type: match.type, tooltip: match.tooltip });
    cursor = match.end;
  });
  if (cursor < text.length) segments.push({ text: text.slice(cursor), type: null });
  return segments;
};
